import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { Navbar } from '../../components/Navbar';
import { ReferralEmailPanel } from '../../components/ReferralEmailPanel';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, User, Save, CheckCircle, MapPin, Home, Plus, Trash2, Star, Lock, Mail, Palette } from 'lucide-react';
import { BackButton } from '../../components/BackButton';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Phase 6.2.2 — addresses are now stored as structured objects
// {line1, city, state, postcode, lat, lng} OR as legacy plain strings.
// This helper safely extracts a single human-readable string for display
// + input controls without crashing if the field is an object.
export function addressToString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const parts = [value.line1, value.apt, value.city, value.state, value.postcode].filter(Boolean);
    return parts.join(', ');
  }
  return String(value);
}

// FastAPI returns Pydantic validation errors as an ARRAY of objects (each
// with {type, loc, msg, input, url}). Rendering that array directly in JSX
// or passing to sonner crashes React. This always returns a single string.
function extractErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
}

// Shared by ProfilePage (Code) and ProfilePageModern.
export function useProfileState() {
  const { user, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: addressToString(user?.address),
    birthday: user?.birthday || '',
  });
  const [loading, setLoading] = useState(false);

  // Saved addresses (customer only)
  const isCustomer = user?.role === 'customer';
  const savedAddressesEnabled = useFlag('customer.saved_addresses', true);
  const showAddresses = isCustomer && savedAddressesEnabled;
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  // "Other" mode is tracked separately so typing a custom label doesn't unset it.
  const [customLabel, setCustomLabel] = useState(false);
  const pickLabelType = (t) => { setCustomLabel(t === 'Other'); setNewLabel(t === 'Other' ? '' : t); };
  // AddressAutocomplete emits a structured object (or null), not a string.
  const [newAddress, setNewAddress] = useState(null);
  const [addingAddress, setAddingAddress] = useState(false);
  const newAddressText = addressToString(newAddress).trim();

  const fetchAddresses = useCallback(async () => {
    if (!showAddresses) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/users/me/addresses`, { withCredentials: true });
      setSavedAddresses(data.addresses || []);
    } catch (_) {
      setSavedAddresses([]);
    }
  }, [showAddresses]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleAddAddress = async () => {
    if (!newAddressText) {
      toast.error('Please pick an address from the dropdown');
      return;
    }
    setAddingAddress(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/users/me/addresses`,
        {
          label: newLabel.trim() || (customLabel ? 'Other' : 'Home'),
          address: newAddressText,
          is_default: savedAddresses.length === 0,
        },
        { withCredentials: true }
      );
      setSavedAddresses(data.addresses || []);
      setNewLabel('');
      setCustomLabel(false);
      setNewAddress(null);
      toast.success('Address saved');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to save address'));
    } finally {
      setAddingAddress(false);
    }
  };

  const handleSetDefault = async (addressId) => {
    const addr = savedAddresses.find(a => a.id === addressId);
    if (!addr) return;
    try {
      // Only flip the is_default flag — DO NOT echo back label/address.
      // Phase 6.2.2 stores addresses as structured objects; echoing them
      // through this PATCH triggers Pydantic 422 errors on the server.
      const { data } = await axios.patch(
        `${API_URL}/api/users/me/addresses/${addressId}`,
        { is_default: true },
        { withCredentials: true }
      );
      setSavedAddresses(data.addresses || []);
      toast.success(`${addr.label} set as default`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to update address'));
    }
  };

  const handleDeleteAddress = async (addressId) => {
    const addr = savedAddresses.find(a => a.id === addressId);
    if (!addr) return;
    if (!window.confirm(`Remove "${addr.label}"?`)) return;
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/users/me/addresses/${addressId}`,
        { withCredentials: true }
      );
      setSavedAddresses(data.addresses || []);
      toast.success('Address removed');
    } catch (err) {
      toast.error('Failed to remove address');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddressChange = (address) => {
    setFormData(prev => ({ ...prev, address }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Feb 2026 — birthday is optional; the server validates the YYYY-MM-DD
      // pattern with no empty-string allowance, so we strip the field when
      // the customer hasn't filled it in (vs. passing '' which 422s).
      const payload = { ...formData };
      if (!payload.birthday) delete payload.birthday;
      await updateProfile(payload);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  return {
    user, formData, loading, handleChange, handleAddressChange, handleSubmit,
    showAddresses, savedAddresses, newLabel, setNewLabel, customLabel, pickLabelType, newAddress, setNewAddress, newAddressText,
    addingAddress, handleAddAddress, handleSetDefault, handleDeleteAddress,
  };
}

export function ProfilePage() {
  const {
    user, formData, loading, handleChange, handleAddressChange, handleSubmit,
    showAddresses, savedAddresses, newLabel, setNewLabel, customLabel, pickLabelType, newAddress, setNewAddress, newAddressText,
    addingAddress, handleAddAddress, handleSetDefault, handleDeleteAddress,
  } = useProfileState();

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="profile-page">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/customer" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} MY_PROFILE
          </h1>
        </div>

        {/* Profile Card */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          {/* User Info */}
          <div className="flex items-center gap-4 pb-6 border-b border-[#2A0F3D] mb-6">
            <div className="w-16 h-16 bg-[#2A0F3D] flex items-center justify-center">
              <User className="w-8 h-8 text-[#39FF14]" />
            </div>
            <div>
              <h2 className="text-[#39FF14] font-mono font-bold text-lg">{user?.name}</h2>
              <p className="text-[#4B6B52] font-mono text-sm">{user?.email}</p>
              <p className="text-[#6B8E73] font-mono text-xs uppercase mt-1">
                {user?.role} ACCOUNT
              </p>
            </div>
          </div>

          {/* Edit Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                FULL_NAME
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="Your name"
                data-testid="profile-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                PHONE_NUMBER
              </Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="555-0100"
                data-testid="profile-phone"
              />
            </div>

            {/* Feb 2026 — birthday for tier-keyed annual bonus. Optional,
                edited only here (NOT on signup). Year is stored but only
                month + day drive the bonus trigger. */}
            <div className="space-y-2">
              <Label htmlFor="birthday" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                BIRTHDAY &middot; <span className="text-[#4B6B52] normal-case tracking-normal">unlocks annual bonus points</span>
              </Label>
              <Input
                id="birthday"
                name="birthday"
                type="date"
                value={formData.birthday || ''}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                data-testid="profile-birthday"
                max={new Date().toISOString().slice(0, 10)}
              />
              <p className="text-[10px] text-[#4B6B52] font-mono">
                We only check month &amp; day to fire the bonus. Year stays private.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                DELIVERY_ADDRESS
              </Label>
              <AddressAutocomplete
                value={formData.address}
                onChange={handleAddressChange}
                placeholder="Start typing your address..."
                data-testid="profile-address"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] rounded-none h-12"
              data-testid="save-profile"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="cursor-blink">█</span> SAVING...
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  SAVE CHANGES
                </>
              )}
            </Button>
          </form>

          {/* Saved Addresses (customers only, gated by feature flag) */}
          {showAddresses && (
            <div className="mt-6 pt-6 border-t border-[#2A0F3D]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[#39FF14] font-mono text-sm font-bold flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  SAVED_ADDRESSES
                </h3>
                <span className="text-[#4B6B52] font-mono text-xs">
                  {savedAddresses.length} saved
                </span>
              </div>

              {savedAddresses.length === 0 ? (
                <p className="text-[#4B6B52] font-mono text-xs mb-4">
                  // No saved addresses yet. Add one below for one-tap checkout.
                </p>
              ) : (
                <div className="space-y-2 mb-4">
                  {savedAddresses.map(addr => (
                    <div
                      key={addr.id}
                      className={`flex items-center justify-between gap-3 p-3 border ${
                        addr.is_default ? 'border-[#39FF14] bg-[rgba(57,255,20,0.05)]' : 'border-[#2A0F3D] bg-[#050003]'
                      }`}
                      data-testid={`saved-address-${addr.id}`}
                    >
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <Home className={`w-4 h-4 mt-0.5 shrink-0 ${addr.is_default ? 'text-[#39FF14]' : 'text-[#6B8E73]'}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-mono text-sm font-bold truncate ${addr.is_default ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}`}>
                              {addr.label}
                            </p>
                            {addr.is_default && (
                              <span className="text-[#39FF14] font-mono text-[10px] uppercase flex items-center gap-1">
                                <Star className="w-3 h-3 fill-[#39FF14]" /> default
                              </span>
                            )}
                          </div>
                          <p className="text-[#6B8E73] font-mono text-xs truncate">{addressToString(addr.address)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!addr.is_default && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(addr.id)}
                            className="border-[#2A0F3D] text-[#6B8E73] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none text-xs font-mono h-7"
                            data-testid={`set-default-${addr.id}`}
                            title="Set as default"
                          >
                            <Star className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteAddress(addr.id)}
                          className="border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF3B30] hover:text-[#FF3B30] rounded-none text-xs font-mono h-7"
                          data-testid={`delete-address-${addr.id}`}
                          title="Remove address"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new address */}
              <div className="space-y-3 bg-[#050003] border border-[#2A0F3D] p-3" data-testid="add-address-form">
                <Label className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">Add new address</Label>

                {/* Step 1: Address autocomplete (the important field) */}
                <div className="space-y-1">
                  <p className="text-[#6B8E73] font-mono text-[10px] uppercase">// Step 1 — Type and pick from suggestions</p>
                  <AddressAutocomplete
                    value={newAddress}
                    onChange={setNewAddress}
                    placeholder="123 Main St, City, ST..."
                    data-testid="new-address-input"
                  />
                </div>

                {/* Step 2: Address type chips */}
                <div className="space-y-1">
                  <p className="text-[#6B8E73] font-mono text-[10px] uppercase">// Step 2 — Pick a type</p>
                  <div className="flex flex-wrap gap-2">
                    {['Home', 'Work', 'Other'].map((t) => {
                      const active = (customLabel ? 'Other' : (newLabel || 'Home')) === t;
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => pickLabelType(t)}
                          data-testid={`new-address-type-${t.toLowerCase()}`}
                          className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wider border transition-colors ${
                            active
                              ? 'bg-[rgba(57,255,20,0.08)] border-[#39FF14] text-[#39FF14]'
                              : 'bg-[#0C0612] border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]/50'
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  {customLabel && (
                    <Input
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      placeholder="Custom label (e.g. Mom's, Gym)"
                      className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none mt-2"
                      data-testid="new-address-label-custom"
                      maxLength={30}
                    />
                  )}
                </div>

                <Button
                  onClick={handleAddAddress}
                  disabled={addingAddress || !newAddressText}
                  className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none font-mono disabled:opacity-40"
                  data-testid="add-address-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {addingAddress ? 'SAVING…' : 'SAVE ADDRESS'}
                </Button>
              </div>
            </div>
          )}

          {/* Account Info */}
          <div className="mt-6 pt-6 border-t border-[#2A0F3D]">
            <h3 className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider mb-4">ACCOUNT_INFO</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-[#4B6B52] font-mono text-sm">Email</span>
                <span className="text-[#A3C7A8] font-mono text-sm">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#4B6B52] font-mono text-sm">Account Type</span>
                <span className="text-[#39FF14] font-mono text-sm uppercase">{user?.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#4B6B52] font-mono text-sm">Member Since</span>
                <span className="text-[#A3C7A8] font-mono text-sm">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Feb 2026 — Modern Card Theme Phase 1: theme toggle
              Only surfaced for customers. Dispatchers/drivers stay pinned
              to the terminal theme since their surfaces aren't re-skinned yet.
              Gated on `customer.modern_theme_toggle` (default OFF) so the
              toggle stays hidden from pilot customers until the launch-day
              announcement email flips the flag ON. Customers who ALREADY
              opted in (ui_theme=modern) still get Modern rendering — this
              flag gates DISCOVERY, not personal preference. */}
          {user?.role === 'customer' && <ThemeTogglePanel />}
          {user?.role === 'customer' && <ReferralEmailPanel />}

          {/* Security */}
          <SecurityPanel />
        </div>
      </div>
    </div>
  );
}

export function useSecurityForm() {
  const [mode, setMode] = useState(null); // null | 'password' | 'email'
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setNewEmail('');
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true },
      );
      toast.success('Password updated. Other sessions have been signed out.');
      reset();
      setMode(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to update password'));
    } finally {
      setBusy(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim()) {
      toast.error('Enter a new email');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-email`,
        { current_password: currentPassword, new_email: newEmail.trim() },
        { withCredentials: true },
      );
      toast.success('Email updated. Use your new email next time you log in.');
      reset();
      setMode(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to update email'));
    } finally {
      setBusy(false);
    }
  };

  return {
    mode, setMode, currentPassword, setCurrentPassword, newPassword, setNewPassword,
    confirmPassword, setConfirmPassword, newEmail, setNewEmail, busy, reset,
    handleChangePassword, handleChangeEmail,
  };
}

export function SecurityPanel() {
  const {
    mode, setMode, currentPassword, setCurrentPassword, newPassword, setNewPassword,
    confirmPassword, setConfirmPassword, newEmail, setNewEmail, busy, reset,
    handleChangePassword, handleChangeEmail,
  } = useSecurityForm();

  return (
    <div className="mt-6 pt-6 border-t border-[#2A0F3D]" data-testid="security-panel">
      <h3 className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider mb-4">SECURITY</h3>

      {mode === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => { reset(); setMode('password'); }} className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono justify-start" data-testid="security-open-change-password">
            <Lock className="w-4 h-4 mr-2" />
            CHANGE PASSWORD
          </Button>
          <Button variant="outline" onClick={() => { reset(); setMode('email'); }} className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono justify-start" data-testid="security-open-change-email">
            <Mail className="w-4 h-4 mr-2" />
            CHANGE EMAIL
          </Button>
        </div>
      )}

      {mode === 'password' && (
        <div className="space-y-3 bg-[#050003] border border-[#2A0F3D] p-4">
          <p className="text-[#6B8E73] font-mono text-[10px] uppercase">// Verify your identity, then set a new password (8+ chars).</p>
          <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] rounded-none" data-testid="security-current-password" autoComplete="current-password" />
          <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (8+ chars)" className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] rounded-none" data-testid="security-new-password" autoComplete="new-password" />
          <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] rounded-none" data-testid="security-confirm-password" autoComplete="new-password" />
          <div className="flex gap-2">
            <Button onClick={handleChangePassword} disabled={busy || !currentPassword || !newPassword || !confirmPassword} className="flex-1 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none font-mono disabled:opacity-40" data-testid="security-submit-password">
              <Save className="w-4 h-4 mr-2" />{busy ? 'UPDATING…' : 'UPDATE PASSWORD'}
            </Button>
            <Button variant="outline" onClick={() => { reset(); setMode(null); }} className="border-[#2A0F3D] text-[#A3C7A8] rounded-none font-mono">
              CANCEL
            </Button>
          </div>
        </div>
      )}

      {mode === 'email' && (
        <div className="space-y-3 bg-[#050003] border border-[#2A0F3D] p-4">
          <p className="text-[#6B8E73] font-mono text-[10px] uppercase">// Re-enter your password to confirm, then set the new email.</p>
          <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] rounded-none" data-testid="security-email-current-password" autoComplete="current-password" />
          <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New email" className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] rounded-none" data-testid="security-new-email" autoComplete="email" />
          <div className="flex gap-2">
            <Button onClick={handleChangeEmail} disabled={busy || !currentPassword || !newEmail} className="flex-1 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none font-mono disabled:opacity-40" data-testid="security-submit-email">
              <Save className="w-4 h-4 mr-2" />{busy ? 'UPDATING…' : 'UPDATE EMAIL'}
            </Button>
            <Button variant="outline" onClick={() => { reset(); setMode(null); }} className="border-[#2A0F3D] text-[#A3C7A8] rounded-none font-mono">
              CANCEL
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * ThemeTogglePanel — Feb 2026 (Modern Card Theme · Phase 1)
 *
 * Customer-only settings panel that lets the user switch between the
 * legacy neon-terminal aesthetic ("Code") and the new card-based light
 * theme ("Modern"). Writes to `user.ui_theme` via ThemeContext, which
 * persists to the backend + localStorage + flips `<html data-theme>`.
 *
 * The two swatches are non-interactive miniatures — clicking selects.
 * A checkmark denotes the current theme. Instant flip, no reload needed.
 */
export function useThemeChoice() {
  const { theme, setTheme } = useTheme();
  const [saving, setSaving] = useState(false);
  // Feb 2026 — Gate discovery on `customer.modern_theme_toggle` (default OFF).
  // Rendering-side kill switch so a customer opening /customer/profile before
  // launch day sees no toggle at all. Customers who already opted in via the
  // pre-flag deploy keep their `ui_theme=modern` and the app still renders
  // the Modern surface for them — this flag only hides the CONTROL that
  // lets someone flip theme. Belt-and-braces alongside the outer guard in
  // the render tree so a future refactor can't accidentally un-gate it.
  const toggleVisible = useFlag('customer.modern_theme_toggle', false);
  const choose = async (next) => {
    if (next === theme || saving) return;
    setSaving(true);
    try {
      await setTheme(next);
      toast.success(next === 'modern' ? 'Modern theme on.' : 'Legacy theme on.');
    } finally {
      setSaving(false);
    }
  };

  return { theme, saving, choose, toggleVisible };
}

function ThemeTogglePanel() {
  const { theme, saving, choose, toggleVisible } = useThemeChoice();
  if (!toggleVisible) return null;

  return (
    <div className="mt-6 pt-6 border-t border-[#2A0F3D]" data-testid="theme-toggle-panel">
      <h3 className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider mb-1 flex items-center gap-2">
        <Palette className="w-3.5 h-3.5" />
        <span>APPEARANCE</span>
      </h3>
      <p className="text-[#4B6B52] font-mono text-xs mb-4">
        // Switch instantly between the classic terminal look and the new card theme.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {/* Code · Legacy */}
        <button
          type="button"
          onClick={() => choose('code')}
          disabled={saving}
          data-testid="theme-swatch-code"
          className={`text-left rounded-none border-2 transition-all ${
            theme === 'code'
              ? 'border-[#39FF14] shadow-[0_0_18px_rgba(57,255,20,0.25)]'
              : 'border-[#2A0F3D] hover:border-[#39FF14]/50'
          } bg-[#050003] overflow-hidden`}
        >
          <div className="h-16 relative" style={{ background: 'linear-gradient(135deg, #050003 0%, #1A0A2A 100%)' }}>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[10px] text-[#39FF14] tracking-wider">
              &gt; ORDERS_
            </span>
          </div>
          <div className="px-3 py-2 flex items-center justify-between">
            <div>
              <div className="font-mono text-[13px] text-[#39FF14] font-bold">Code</div>
              <div className="font-mono text-[10px] text-[#6B8E73]">Legacy</div>
            </div>
            {theme === 'code' && (
              <CheckCircle className="w-4 h-4 text-[#39FF14]" data-testid="theme-active-code" />
            )}
          </div>
        </button>

        {/* Modern · New */}
        <button
          type="button"
          onClick={() => choose('modern')}
          disabled={saving}
          data-testid="theme-swatch-modern"
          className={`text-left rounded-none border-2 transition-all ${
            theme === 'modern'
              ? 'border-[#39FF14] shadow-[0_0_18px_rgba(57,255,20,0.25)]'
              : 'border-[#2A0F3D] hover:border-[#39FF14]/50'
          } bg-white overflow-hidden`}
        >
          <div className="h-16 relative" style={{ background: 'linear-gradient(135deg, #F7F5F0 0%, #FFFFFF 100%)' }}>
            <span className="absolute left-3 top-[26%] font-semibold text-[12px] text-[#047857]" style={{ fontFamily: 'Inter, sans-serif' }}>
              Orders
            </span>
            <span className="absolute right-3 top-2 h-3 w-9 rounded-[4px]" style={{ background: '#B08D57' }} />
            <span className="absolute left-3 bottom-2 text-[10px] text-[#3D4A44]" style={{ fontFamily: 'Inter, sans-serif' }}>
              from $45
            </span>
          </div>
          <div className="px-3 py-2 flex items-center justify-between bg-white">
            <div>
              <div className="text-[13px] text-[#0F1712] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Modern</div>
              <div className="text-[10px] text-[#6E7A72]" style={{ fontFamily: 'Inter, sans-serif' }}>New</div>
            </div>
            {theme === 'modern' && (
              <CheckCircle className="w-4 h-4 text-[#047857]" data-testid="theme-active-modern" />
            )}
          </div>
        </button>
      </div>
      {saving && (
        <p className="text-[#4B6B52] font-mono text-[11px] mt-3">Saving…</p>
      )}
    </div>
  );
}
